import { motion } from "framer-motion";
import styled from "styled-components";
import { BsFillStarFill } from "react-icons/bs";
import { formatVoteAverage, getPosterUrl, getBackdropUrl } from "../../utils/movie";

const Card = styled(motion.button)`
  position: relative;
  display: grid;
  align-content: end;
  width: 100%;
  min-width: 0;
  padding: 0;
  border-radius: ${({ theme }) => theme.radii.md};
  overflow: hidden;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadows.soft};
  aspect-ratio: 2 / 3;
  text-align: left;

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.4);
    outline-offset: 3px;
  }
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at top, rgba(229, 9, 20, 0.25), transparent 35%),
    linear-gradient(180deg, #232323, #111111);
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  align-content: end;
  gap: 0.6rem;
  padding: 1rem;
  background: linear-gradient(180deg, rgba(9, 9, 9, 0.02) 0%, rgba(9, 9, 9, 0.72) 58%, rgba(9, 9, 9, 0.98) 100%);
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const Genres = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textMuted};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const Rating = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colors.white};
`;

function MovieCard({ movie, onSelect }) {
  const posterUrl = getPosterUrl(movie.posterPath) || getBackdropUrl(movie.backdropPath, "w780");
  const genreSummary = movie.genreNames?.slice(0, 2).join(" / ") || "Catalogo em destaque";

  return (
    <Card
      type="button"
      onClick={() => onSelect(movie.id)}
      whileHover={{ scale: 1.06, y: -8 }}
      transition={{ duration: 0.24, ease: "easeOut", delay: 0.04 }}
      aria-label={`Abrir detalhes de ${movie.title}`}
    >
      {posterUrl ? (
        <Poster src={posterUrl} alt={movie.title} loading="lazy" />
      ) : (
        <Placeholder aria-hidden />
      )}
      <Overlay>
        <Meta>
          <span>{movie.releaseYear}</span>
          <Rating>
            <BsFillStarFill size={12} color="#f5c518" aria-hidden />
            {formatVoteAverage(movie.rating)}
          </Rating>
        </Meta>
        <Title>{movie.title}</Title>
        <Genres>{genreSummary}</Genres>
      </Overlay>
    </Card>
  );
}

export default MovieCard;
